import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Box,
  Typography,
  Avatar,
} from '@mui/material';
import {
  Home as HomeIcon,
  Notifications as NotificationsIcon,
  SmartToy as SmartToyIcon,
  PlayCircle as PlayCircleIcon,
  Share as ShareIcon,
  RateReview as RateReviewIcon,
  Logout as LogoutIcon,
  ChevronRight as ChevronRightIcon,
  Bolt as BoltIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Category as CategoryIcon,
  Psychology as PsychologyIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { getLocationOn, getUserFromStorage, uppercaseFirstLetter, Colors } from '../../helpers/navigationHelpers';
import { deleteTokens } from '../../helpers/tokenStorage';
import styles from './CustomDrawer.module.css';

const CustomDrawer = ({ onClose }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [locationOn, setLocationOn] = useState(false);

  useEffect(() => {
    setUser(getUserFromStorage());
    setLocationOn(getLocationOn());
  }, []);

  const primaryColor = locationOn ? Colors.CUSTOM_YELLOW : Colors.PRIMARY;

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      await deleteTokens();
      localStorage.clear();
      navigate('/landing');
      if (onClose) onClose();
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleShare = () => {
    // Share functionality
    if (navigator.share) {
      navigator.share({
        title: 'Tellmiz',
        text: 'Check out Tellmiz - A Social Application',
        url: window.location.origin,
      }).catch(err => console.log('Error sharing:', err));
    } else {
      // Fallback for browsers that don't support Web Share API
      alert('Share URL: ' + window.location.origin);
    }
    if (onClose) onClose();
  };

  const handleReview = () => {
    // Review functionality
    alert('Leave us a review!');
    if (onClose) onClose();
  };

  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/app/feed' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/app/notifications' },
    { text: 'Tellmiz AI', icon: <SmartToyIcon />, path: '/app/ai-chat' },
    { text: 'Reels', icon: <PlayCircleIcon />, path: '/app/reels-feed' },
  ];

  // Admin-only menu items
  const adminMenuItems = [
    { text: 'Admins', icon: <AdminPanelSettingsIcon />, path: '/users' },
    { text: 'Categories', icon: <CategoryIcon />, path: '/categories' },
    { text: 'Consultants', icon: <PsychologyIcon />, path: '/consultants' },
    { text: 'Config', icon: <SettingsIcon />, path: '/config' },
  ];

  return (
    <div className={styles.drawerContainer}>
      {/* User Profile Section */}
      <Box
        className={styles.profileSection}
        sx={{ backgroundColor: primaryColor }}
        onClick={() => handleNavigation('/app/profile')}
      >
        {user?.pictureURL ? (
          <Avatar
            src={user.pictureURL}
            alt={user.username}
            sx={{ width: 80, height: 80, marginBottom: 1 }}
          />
        ) : (
          <Avatar
            sx={{
              width: 80,
              height: 80,
              marginBottom: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              fontSize: 32,
            }}
          >
            {user?.username?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
        )}
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
          {uppercaseFirstLetter(user?.username || 'User')}
          <BoltIcon sx={{ fontSize: 20, color: Colors.CUSTOM_YELLOW, ml: 0.5 }} />
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          {user?.email || ''}
        </Typography>
      </Box>

      {/* Menu Items */}
      <List sx={{ flex: 1, paddingTop: 2 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              paddingY: 1.5,
              '&:hover': {
                backgroundColor: locationOn ? 'rgba(255, 214, 0, 0.1)' : 'rgba(0, 84, 153, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#666' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontSize: 15 }}
            />
          </ListItemButton>
        ))}
        
        {/* Admin-only menu items */}
        {user?.isAdmin && adminMenuItems.map((item) => (
          <ListItemButton
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              paddingY: 1.5,
              '&:hover': {
                backgroundColor: locationOn ? 'rgba(255, 214, 0, 0.1)' : 'rgba(0, 84, 153, 0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: '#666' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontSize: 15 }}
            />
          </ListItemButton>
        ))}
      </List>

      {/* AI Chat Section */}
      <Box className={styles.aiChatSection}>
        <Box
          className={styles.aiChatContainer}
          onClick={() => handleNavigation('/app/ai-chat')}
        >
          <Box
            className={styles.aiChatGradient}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            }}
          >
            <Box className={styles.aiChatContent}>
              <SmartToyIcon sx={{ fontSize: 28, color: 'white' }} />
              <Box sx={{ flex: 1, marginLeft: 2 }}>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold' }}>
                  AI Assistant
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  Chat with AI • Get instant help
                </Typography>
              </Box>
              <ChevronRightIcon sx={{ fontSize: 24, color: 'white' }} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom Section */}
      <Box className={styles.bottomSection}>
        <Divider />
        <ListItemButton onClick={handleShare} sx={{ paddingY: 1.5 }}>
          <ListItemIcon sx={{ color: '#666' }}>
            <ShareIcon />
          </ListItemIcon>
          <ListItemText
            primary="Tell a Friend"
            primaryTypographyProps={{ fontSize: 15 }}
          />
        </ListItemButton>

        <ListItemButton onClick={handleReview} sx={{ paddingY: 1.5 }}>
          <ListItemIcon sx={{ color: '#666' }}>
            <RateReviewIcon />
          </ListItemIcon>
          <ListItemText
            primary="Leave Us a Review"
            primaryTypographyProps={{ fontSize: 15 }}
          />
        </ListItemButton>

        <ListItemButton onClick={handleLogout} sx={{ paddingY: 1.5 }}>
          <ListItemIcon sx={{ color: 'red' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Log Out"
            primaryTypographyProps={{ fontSize: 15, color: 'red' }}
          />
        </ListItemButton>
      </Box>
    </div>
  );
};

export default CustomDrawer;













